import React, { FC, Fragment, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/shared/page-title/page-title.component';
import { Alert, Box, Fab, Tab, Tabs, Typography } from '@mui/material';
import LabDetailsForm from './lab-details-form/lab-details-form.components';
import { CurrentJobs, Organization } from 'models/organization';
import AvailableConstructions from './available-constructions/available-constructions.components';
import {
    modifyOrganizationLocaleFormatTemplate,
    updateGeneralInformationTemplate,
    updateLabProfileTemplate,
    updateSupplierProfileTemplate,
} from 'apollo/templates/organization';
import { useMutation, useQuery } from '@apollo/client';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { labSettingsTemplate } from 'apollo/templates/organization/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { LabDetailsFormData } from 'models/forms';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { Currency } from 'models/country';
import { DEFAULT_LOCALE_FORMAT, LocaleFormat } from 'models/locale-format';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import LocaleFormatPicker from 'components/shared/locale-format-picker/locale-format-picker.component';
import InfoText from 'components/shared/info-text.component';
import Loader from 'components/shared/loader/loader.component';
import { ProfileText, SupplierProfile } from 'models/organization-profile';
import GeneralProfileTab, { ProfileMedia } from './profile/general-profile-tab.component';
import ClinicProfileTab from './profile/clinic-profile-tab.component';
import SupplierProfileTab from './profile/supplier-profile-tab.component';
import {
    EMPTY_SUPPLIER_PROFILE,
    clinicTextsOf,
    labLanguageOf,
    mediaOf,
    supplierProfileOf,
} from './profile/lab-profile-state';

type GeneralInformationTab = 'general' | 'clinic' | 'supplier';

/**
 * Lab settings → General information. DD-3689: three tabs, saved together — General (company
 * details, logo and media, locale), Clinic profile (what dentists read, the constructions the lab
 * offers and their delivery days) and Supplier profile (what other labs read). Every tab stays
 * mounted while hidden, so unsaved edits survive a tab switch and Save always sends the whole form.
 */
const GeneralSettings: FC = () => {
    const { t: translate } = useTranslation(['lab-settings', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);
    const [tab, setTab] = useState<GeneralInformationTab>('general');
    const [currentLab, setCurrentLab] = useState<Organization>();
    const [exports, setExports] = useState({
        enable: false,
        additionalDeliveryDays: 0,
    });
    const [isLabDetailsInvalid, setIsLabDetailsInvalid] = useState(false);
    const [currentJobs, setCurrentJobs] = useState<CurrentJobs[]>([]);
    const [labDetails, setLabDetails] = useState<LabDetailsFormData>();
    const [currency, setCurrency] = useState<Currency>({} as Currency);
    const [localeFormat, setLocaleFormat] = useState<LocaleFormat>(DEFAULT_LOCALE_FORMAT);
    const [media, setMedia] = useState<ProfileMedia>();
    const [clinicTexts, setClinicTexts] = useState<ProfileText[]>([]);
    const [supplierProfile, setSupplierProfile] = useState<SupplierProfile>(EMPTY_SUPPLIER_PROFILE);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string>();

    const { loading } = useQuery(labSettingsTemplate, {
        onError(error) {
            showBoundary(error);
        },
        onCompleted({ currentLab }: { currentLab: Organization }) {
            setCurrentLab(currentLab);
            setCurrentJobs(currentLab.currentJobs);
            setExports({
                enable: currentLab.export.enable as boolean,
                additionalDeliveryDays: currentLab.export.additionalDeliveryDays as number,
            });
            setCurrency({ code: currentLab.sourceCurrency || '' } as Currency);
            if (currentLab.localeFormat) {
                setLocaleFormat(currentLab.localeFormat);
            }
            setMedia(mediaOf(currentLab));
            setClinicTexts(clinicTextsOf(currentLab, labLanguageOf(currentLab)));
            setSupplierProfile(supplierProfileOf(currentLab));
        },
    });

    const [updateGeneralInformation] = useMutation(updateGeneralInformationTemplate);
    const [modifyOrganizationLocaleFormat] = useMutation(modifyOrganizationLocaleFormatTemplate, {
        onCompleted: ({ modifyOrganizationLocaleFormat }) => {
            const updatedOrg = currentOrgVar();
            currentOrgVar({ ...updatedOrg, localeFormat: modifyOrganizationLocaleFormat.localeFormat });
        },
    });
    const [updateLabProfile] = useMutation(updateLabProfileTemplate);
    const [updateSupplierProfile] = useMutation(updateSupplierProfileTemplate);

    const handleAdditionalDaysChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setExports((prev) => {
            return {
                ...prev,
                additionalDeliveryDays: parseInt(event.target.value) || 0,
            };
        });
    };

    const handleProductionAbroadChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setExports({
            ...exports,
            enable: event.target.checked,
        });
    };

    const handleJobChange = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const updatedCurrentJobs = JSON.parse(JSON.stringify(currentJobs));
        updatedCurrentJobs[index].active = event.target.checked;
        setCurrentJobs(updatedCurrentJobs);
    };

    const handleDeliveryDaysChange = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const updatedCurrentJobs = structuredClone(currentJobs);
        updatedCurrentJobs[index].deliveryDays = parseInt(event.target.value) || 0;
        setCurrentJobs(updatedCurrentJobs);
    };

    const handleSaveChanges = async () => {
        if (!media) return;
        const generalInformationData = {
            name: labDetails?.name,
            publishLab: labDetails?.publishLab,
            currentJobs: currentJobs,
            export: exports,
            sourceCurrency: currency.code,
            contact: {
                address: labDetails?.address,
                communication: {
                    phone: labDetails?.phone,
                    email: labDetails?.email,
                    site: labDetails?.site,
                },
            },
        };
        const labProfileSettings = {
            logoImage: media.logoImage || undefined,
            staffImage: media.staffImage || undefined,
            hexCode: media.hexCode,
            textCode: media.textCode,
            profileTexts: clinicTexts,
        };

        setIsSaving(true);
        setSaveError(undefined);
        try {
            await Promise.all([
                updateGeneralInformation({ variables: { generalInformationData } }),
                modifyOrganizationLocaleFormat({ variables: { input: localeFormat } }),
                updateLabProfile({ variables: { labProfileSettings } }),
                updateSupplierProfile({ variables: { supplierProfile } }),
            ]);
            setAppToasterContent({ isOpen: true, severity: 'success', message: translate('common:CHANGES_SAVED') });
        } catch (error) {
            setSaveError((error as Error).message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleLabDetailsChange = (labForm: LabDetailsFormData, isFormValid: boolean) => {
        setIsLabDetailsInvalid(!isFormValid);
        setLabDetails(labForm);
    };

    if (loading || !currentLab || !media) return <Loader />;

    const labLanguage = labLanguageOf(currentLab);

    return (
        <Fragment>
            {saveError ? <AlertSnackbar type="error" message={saveError} /> : undefined}
            <PageTitle headingStyle="h2" title={translate('lab-settings:GENERAL_INFO')} dbId={currentLab._id} />
            <Box mb={3} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tab} onChange={(_event, value: GeneralInformationTab) => setTab(value)}>
                    <Tab
                        value="general"
                        label={translate('lab-settings:PROFILE_TAB_GENERAL')}
                        data-test="general-information.general-tab"
                    />
                    <Tab
                        value="clinic"
                        label={translate('lab-settings:PROFILE_TAB_CLINIC')}
                        data-test="general-information.clinic-tab"
                    />
                    <Tab
                        value="supplier"
                        label={translate('lab-settings:PROFILE_TAB_SUPPLIER')}
                        data-test="general-information.supplier-tab"
                    />
                </Tabs>
            </Box>

            {isLabDetailsInvalid && tab !== 'general' ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {translate('lab-settings:GENERAL_TAB_HAS_ERRORS')}
                </Alert>
            ) : undefined}

            <Box hidden={tab !== 'general'}>
                <Box mb={3}>
                    <LabDetailsForm
                        lab={currentLab}
                        onChange={handleLabDetailsChange}
                        currency={currency}
                        onCurrencySelect={(currencyObj) => {
                            setCurrency(currencyObj);
                        }}
                    />
                </Box>
                <Box mb={3}>
                    <GeneralProfileTab media={media} onChange={setMedia} />
                </Box>
                <Box mb={3}>
                    <Typography variant="h3" sx={{ marginBottom: 1 }}>
                        {translate('lab-settings:LOCALE_FORMAT_HEADING')}
                    </Typography>
                    <InfoText text={translate('lab-settings:LOCALE_FORMAT_INFO')} />
                    <Box mt={2}>
                        <LocaleFormatPicker value={localeFormat} onChange={setLocaleFormat} />
                    </Box>
                </Box>
            </Box>

            <Box hidden={tab !== 'clinic'}>
                <Box mb={3}>
                    <ClinicProfileTab texts={clinicTexts} onChange={setClinicTexts} labLanguage={labLanguage} />
                </Box>
                <Box mb={3}>
                    <AvailableConstructions
                        exports={exports}
                        currentJobs={currentJobs}
                        onJobChange={handleJobChange}
                        onAdditionalDaysChange={handleAdditionalDaysChange}
                        onProductionAbroadChange={handleProductionAbroadChange}
                        onDeliveryDaysChange={handleDeliveryDaysChange}
                    />
                </Box>
            </Box>

            <Box hidden={tab !== 'supplier'} mb={3}>
                <SupplierProfileTab profile={supplierProfile} onChange={setSupplierProfile} labLanguage={labLanguage} />
            </Box>

            <Fab
                sx={{ position: 'fixed', left: '50%', bottom: 20, zIndex: 100 }}
                variant="extended"
                onClick={handleSaveChanges}
                disabled={isLabDetailsInvalid || isSaving}
                color="primary"
                data-test="general-information.save"
            >
                <SaveOutlinedIcon sx={{ marginRight: 1 }} />
                {translate('lab-settings:SAVE_CHANGES')}
            </Fab>
        </Fragment>
    );
};

export default GeneralSettings;
